import { STATE_MAP } from 'features/store/constants';

import type { IRestaurant } from '@rbi-ctg/store';

import type { FormattedStoreAddress, RestaurantDetailItem } from './types';

type RestaurantDetailItemConfig = RestaurantDetailItem & {
  shouldShow: boolean;
};

const enabledBySanity = (value: boolean | null | undefined) => value === true;

const isUSState = (value: string): value is keyof typeof STATE_MAP => value in STATE_MAP;

const getUSState = (stateProvince: string): string =>
  isUSState(stateProvince) ? STATE_MAP[stateProvince] : stateProvince;

const visibleItems = (items: RestaurantDetailItemConfig[]): RestaurantDetailItem[] =>
  items.reduce<RestaurantDetailItem[]>((accumulator, { shouldShow, icon, label, testID }) => {
    if (shouldShow) {
      accumulator.push({ icon, label, testID });
    }

    return accumulator;
  }, []);

export const getFormattedStoreAddress = (
  physicalAddress: IRestaurant['physicalAddress']
): FormattedStoreAddress | null => {
  const streetAddress = physicalAddress?.address1 ?? '';
  const city = physicalAddress?.city;
  const state = getUSState(physicalAddress?.stateProvince ?? '');
  const postalCode = physicalAddress?.postalCode?.split('-')[0];
  const statePostalCode = [state, postalCode].filter(Boolean).join(' ');
  const cityStatePostalCode = [city, statePostalCode].filter(Boolean).join(', ');

  if (!streetAddress && !cityStatePostalCode) {
    return null;
  }

  return {
    cityStatePostalCode,
    streetAddress,
  };
};

export const formatStorePhoneNumber = (phoneNumber: string | null | undefined): string | null => {
  if (!phoneNumber) {
    return null;
  }

  const digits = phoneNumber.replace(/\D/g, '');
  const nationalNumber = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (nationalNumber.length !== 10) {
    return phoneNumber;
  }

  return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
};

export const getStoreServiceAvailabilityItems = (store: IRestaurant): RestaurantDetailItem[] =>
  visibleItems([
    {
      icon: 'driveThru',
      label: {
        defaultMessage: 'Drive Thru',
        key: 'driveThru',
      },
      shouldShow: enabledBySanity(store.hasDriveThru),
      testID: 'service-drive-thru',
    },
    {
      icon: 'restaurant',
      label: {
        defaultMessage: 'Dine In',
        key: 'dineIn',
      },
      shouldShow: enabledBySanity(store.hasDineIn),
      testID: 'service-dine-in',
    },
    {
      icon: 'delivery',
      label: {
        defaultMessage: 'Delivery',
        key: 'delivery',
      },
      shouldShow: enabledBySanity(store.hasDelivery),
      testID: 'service-delivery',
    },
  ]);

export const getStoreAmenityItems = (
  store: IRestaurant,
  isMobileOrderingAvailable: boolean
): RestaurantDetailItem[] =>
  visibleItems([
    {
      icon: 'wifi',
      label: {
        defaultMessage: 'Wifi',
        key: 'wifi',
      },
      shouldShow: enabledBySanity(store.hasWifi),
      testID: 'amenity-wifi',
    },
    {
      icon: 'parking',
      label: {
        defaultMessage: 'Parking Lot',
        key: 'parkingLot',
      },
      shouldShow: enabledBySanity(store.hasParking),
      testID: 'amenity-parking',
    },
    {
      icon: 'mobile',
      label: {
        defaultMessage: 'Mobile Ordering & Pickup',
        key: 'mobileOrderingAndPickup',
      },
      shouldShow: isMobileOrderingAvailable,
      testID: 'amenity-mobile-ordering',
    },
    {
      icon: 'playground',
      label: {
        defaultMessage: 'Indoor Play Place',
        key: 'indoorPlayPlace',
      },
      shouldShow: enabledBySanity(store.hasPlayground),
      testID: 'amenity-playground',
    },
  ]);
